import nacl from 'tweetnacl';
import { encodeBase64, decodeBase64 } from 'tweetnacl-util';
import { makeWatchdog } from './wsWatchdog.mjs';
import { serverWsBase } from './serverBase.mjs';

let ws = null;
let reconnectTimer = null;
// Answers the question readyState cannot: is this socket alive, or merely open? (wsWatchdog.mjs)
let watchdog = null;
let currentToken = null;
let currentPrivacyMode = 0;
let hasConnectedBefore = false;
// Which device this client is, once the ratchet has published its bundle. Kept
// at module level because a reconnect must announce it again: the server holds
// it on the socket, and the socket is what died.
let knownDeviceId = null;
const listeners = new Map();

// ─── NaCl encryption state ─────────────────────────────────
let naclKeyPair = null;

/** 'foreground' only when the page is actually visible to the user. */
function currentAppState() {
  if (typeof document === 'undefined') return 'foreground';
  return document.visibilityState === 'visible' ? 'foreground' : 'background';
}

let naclSharedKey = null;
let naclSendNonce = 0;

function makeNonce(counter) {
  const nonce = new Uint8Array(24);
  // Write counter as little-endian into first 8 bytes
  let c = counter;
  for (let i = 0; i < 8; i++) {
    nonce[i] = c & 0xff;
    c = Math.floor(c / 256);
  }
  return nonce;
}

function encryptMessage(msg) {
  if (!naclSharedKey) return JSON.stringify(msg);
  const plaintext = new TextEncoder().encode(JSON.stringify(msg));
  const nonce = makeNonce(naclSendNonce++);
  const encrypted = nacl.secretbox(plaintext, nonce, naclSharedKey);
  return JSON.stringify({
    type: 'encrypted',
    nonce: encodeBase64(nonce),
    data: encodeBase64(encrypted),
  });
}

function decryptMessage(data) {
  if (!naclSharedKey) return data;
  try {
    const msg = JSON.parse(data);
    if (msg.type !== 'encrypted') return data;
    const nonce = decodeBase64(msg.nonce);
    const ciphertext = decodeBase64(msg.data);
    const plaintext = nacl.secretbox.open(ciphertext, nonce, naclSharedKey);
    if (!plaintext) {
      console.error('[WS-NACL] Decryption failed');
      return data;
    }
    return new TextDecoder().decode(plaintext);
  } catch (e) {
    return data;
  }
}

// Tell the server the moment we go background/foreground. Without this the
// server only learns the state at connect time, and a long-lived socket keeps
// its first answer — which is exactly how a backgrounded app kept claiming to
// be in front of the user.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify({ type: 'app_state', app_state: currentAppState() }));
      } catch (e) { /* socket died mid-flight — reconnect will report state */ }
    }
  });
}

export const wsService = {
  /**
   * Tell the server which device this socket belongs to.
   *
   * Called when the ratchet finishes publishing, which is after the socket is
   * already up. Remembered, so every later reconnect says it during auth
   * rather than leaving a window where messages route to the fallback.
   */
  announceDevice(deviceId) {
    if (!deviceId || knownDeviceId === deviceId) return;
    knownDeviceId = deviceId;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try { ws.send(JSON.stringify({ type: 'device_id', deviceId })); } catch (e) { /* the reconnect will say it */ }
    }
  },

  connect(token, privacyMode = 0) {
    if (ws && ws.readyState === WebSocket.OPEN) return;
    // Close stale WS in CONNECTING state to avoid duplicate connections
    if (ws && ws.readyState === WebSocket.CONNECTING) {
      ws.close();
    }
    currentToken = token;
    currentPrivacyMode = privacyMode;

    // Reset NaCl state on new connection
    naclSharedKey = null;
    naclSendNonce = 0;

    const wsUrl = process.env.REACT_APP_WS_URL || `${serverWsBase()}/ws`;

    ws = new WebSocket(wsUrl);
    ws.binaryType = 'arraybuffer';

    ws.onopen = () => {
      clearTimeout(reconnectTimer);

      // Report the REAL state. This used to be hardcoded to 'foreground', and
      // with background keep-alive the JS keeps running and reconnecting, so the
      // server was told "foreground" forever — and suppressed every push on the
      // grounds that the user was already looking at the app.
      const appState = currentAppState();

      // A browser tab is not a phone. The server suppresses a push when it
      // believes the person is already looking at the app — but it only knew
      // "some socket of this user says foreground", and a laptop with the tab
      // open silenced the phone in the person's pocket. Only a client that
      // could have received the push may cancel it, so say which we are.
      const pushCapable = !!window.VoxioxNative;

      // Which device this socket is. A ciphertext belongs to one device and no
      // other can open it, so the server routes by this rather than handing
      // every message to whichever socket of ours connected last. It may not
      // exist yet — the ratchet publishes its bundle a moment after the socket
      // opens — and `announceDevice` fills it in when it does.
      const deviceId = knownDeviceId;

      if (privacyMode >= 3) {
        // NaCl mode: generate ephemeral keypair and send publicKey with auth
        naclKeyPair = nacl.box.keyPair();
        ws.send(JSON.stringify({
          type: 'auth',
          token,
          publicKey: encodeBase64(naclKeyPair.publicKey),
          app_state: appState,
          client: 'js',
          push_capable: pushCapable,
          device_id: deviceId,
        }));
      } else {
        ws.send(JSON.stringify({ type: 'auth', token, app_state: appState, client: 'js', push_capable: pushCapable, device_id: deviceId }));
      }

      // The heartbeat is a question now, not a report: sent every 30 s (it
      // still refreshes last_seen on the server), and if NOTHING comes back
      // within 15 s the socket is closed on purpose and reconnected — an
      // open socket that carries nothing is how the app went deaf behind the
      // relay (2026-09-02). Any inbound frame counts as an answer.
      const sock = ws;
      if (watchdog) watchdog.stop();
      watchdog = makeWatchdog({
        send: (m) => { if (sock.readyState === WebSocket.OPEN) sock.send(naclSharedKey ? encryptMessage(m) : JSON.stringify(m)); },
        onDead: (why) => {
          console.warn('[WS] socket is open but dead —', why, '— reconnecting');
          try { sock.close(); } catch (e) { /* already gone */ }
          if (ws === sock) ws.onclose && ws.onclose();
        },
      });
      watchdog.start();
    };

    ws.onmessage = (event) => {
      if (watchdog) watchdog.seen();
      // Binary frame = tunnel audio relay
      if (event.data instanceof ArrayBuffer) {
        const bytes = new Uint8Array(event.data);
        if (bytes.length > 0 && bytes[0] === 0x01) {
          const cbs = listeners.get('tunnel_audio_binary');
          if (cbs) cbs.forEach(cb => cb(bytes.subarray(1)));
        }
        return;
      }

      // JSON text messages
      try {
        // Try to decrypt if in NaCl mode
        const rawData = naclSharedKey ? decryptMessage(event.data) : event.data;
        const msg = JSON.parse(rawData);

        // Handle NaCl key exchange response
        if (msg.type === 'auth_ok' && msg.publicKey && naclKeyPair) {
          const serverPubKey = decodeBase64(msg.publicKey);
          naclSharedKey = nacl.box.before(serverPubKey, naclKeyPair.secretKey);
          console.log('[WS-NACL] Encrypted channel established');
        }

        // Emit 'ws_reconnected' on successful re-auth (so UI can refetch missed messages)
        if (msg.type === 'auth_ok' && hasConnectedBefore) {
          const rcbs = listeners.get('ws_reconnected');
          if (rcbs) rcbs.forEach(cb => cb(msg));
        }
        if (msg.type === 'auth_ok') hasConnectedBefore = true;

        const cbs = listeners.get(msg.type);
        if (cbs) cbs.forEach(cb => cb(msg));
      } catch (e) {
        console.error('[WS] Message parse error:', e, 'raw:', typeof event.data === 'string' ? event.data.slice(0, 200) : '(binary)');
      }
    };

    ws.onclose = () => {
      if (watchdog) { watchdog.stop(); watchdog = null; }
      if (ws) ws.onclose = null;   // a watchdog-forced close must not run this twice
      ws = null;
      naclSharedKey = null;
      naclSendNonce = 0;
      if (currentToken) {
        reconnectTimer = setTimeout(() => this.connect(currentToken, currentPrivacyMode), 3000);
      }
    };

    ws.onerror = () => {
      if (ws) ws.close();
    };
  },

  /**
   * Whether anything handed to send() would actually leave the device.
   *
   * send() drops what it cannot deliver — it has nowhere to put it — so the
   * job queue has to ask before it treats a job as done. Without this, "sent"
   * meant "handed to a function that may have thrown it away".
   */
  isOpen() { return !!(ws && ws.readyState === WebSocket.OPEN); },

  /**
   * Something that should have come back did not (a send with no chat_sent,
   * a return from the background): ask the socket now whether it is alive
   * rather than waiting for the next heartbeat.
   */
  suspect(reason) {
    if (!watchdog || !this.isOpen()) return;
    console.log('[WS] probing the socket —', reason);
    watchdog.probe();
  },

  send(msg) {
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(naclSharedKey ? encryptMessage(msg) : JSON.stringify(msg));
    }
  },

  sendBinary(arrayBuffer) {
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    if (ws.bufferedAmount > 65536) return; // Backpressure: drop frame if >64KB queued (HOL mitigation)
    ws.send(arrayBuffer);
  },

  on(type, callback) {
    if (!listeners.has(type)) listeners.set(type, new Set());
    listeners.get(type).add(callback);
    return () => listeners.get(type).delete(callback);
  },

  // Dispatch an event to listeners (used by native WS bridge to forward messages)
  dispatch(type, data) {
    const cbs = listeners.get(type);
    if (cbs) cbs.forEach(cb => cb(data));
  },

  disconnect() {
    currentToken = null;
    currentPrivacyMode = 0;
    naclSharedKey = null;
    naclKeyPair = null;
    naclSendNonce = 0;
    hasConnectedBefore = false;
    clearTimeout(reconnectTimer);
    if (ws) { ws.close(); ws = null; }
    listeners.clear();
  }
};

// ─── Auto-reconnect on iOS foreground return ──────────────────
// AppDelegate dispatches 'app-foreground' when app returns from background.
// If WS was lost in background, reconnect immediately with same token & privacy mode.
document.addEventListener('app-foreground', () => {
  if (currentToken && (!ws || ws.readyState !== WebSocket.OPEN)) {
    console.log('[WS] App returned to foreground — reconnecting');
    clearTimeout(reconnectTimer);
    wsService.connect(currentToken, currentPrivacyMode);
  } else if (ws) {
    // Open is not alive: the socket that survived the background on paper
    // is probed right away, so a dead one is replaced within 15 s.
    wsService.suspect('returned to foreground');
  }
});
